import countriesData from 'assets/countries.json';

interface Currency {
  code: string;
  symbol: string;
  name: string;
}

interface CurrencyConfig {
  symbol: string;
  locale: string;
  symbolPosition: 'before' | 'after';
}

// Locale mapping for common currencies (for proper number formatting)
const CURRENCY_LOCALES: Record<
  string,
  { locale: string; position: 'before' | 'after' }
> = {
  EUR: { locale: 'de-DE', position: 'before' },
  USD: { locale: 'en-US', position: 'before' },
  GBP: { locale: 'en-GB', position: 'before' },
  CHF: { locale: 'de-CH', position: 'after' },
  JPY: { locale: 'ja-JP', position: 'before' },
  CNY: { locale: 'zh-CN', position: 'before' },
  AUD: { locale: 'en-AU', position: 'before' },
  CAD: { locale: 'en-CA', position: 'before' },
  INR: { locale: 'en-IN', position: 'before' },
  PLN: { locale: 'pl-PL', position: 'after' },
  SEK: { locale: 'sv-SE', position: 'after' },
  NOK: { locale: 'nb-NO', position: 'after' },
  DKK: { locale: 'da-DK', position: 'after' },
  CZK: { locale: 'cs-CZ', position: 'after' },
  HUF: { locale: 'hu-HU', position: 'after' },
  RON: { locale: 'ro-RO', position: 'after' },
  BGN: { locale: 'bg-BG', position: 'after' },
  HRK: { locale: 'hr-HR', position: 'after' },
  RSD: { locale: 'sr-RS', position: 'after' },
  RUB: { locale: 'ru-RU', position: 'after' },
  UAH: { locale: 'uk-UA', position: 'after' },
  TRY: { locale: 'tr-TR', position: 'after' },
  BRL: { locale: 'pt-BR', position: 'before' },
  MXN: { locale: 'es-MX', position: 'before' },
  ARS: { locale: 'es-AR', position: 'before' },
  CLP: { locale: 'es-CL', position: 'before' },
  COP: { locale: 'es-CO', position: 'before' },
  PEN: { locale: 'es-PE', position: 'before' },
  ZAR: { locale: 'en-ZA', position: 'before' },
  NZD: { locale: 'en-NZ', position: 'before' },
  SGD: { locale: 'en-SG', position: 'before' },
  HKD: { locale: 'zh-HK', position: 'before' },
  KRW: { locale: 'ko-KR', position: 'before' },
  THB: { locale: 'th-TH', position: 'before' },
  MYR: { locale: 'ms-MY', position: 'before' },
  IDR: { locale: 'id-ID', position: 'before' },
  PHP: { locale: 'en-PH', position: 'before' },
  VND: { locale: 'vi-VN', position: 'after' },
  ILS: { locale: 'he-IL', position: 'before' },
  AED: { locale: 'ar-AE', position: 'before' },
  SAR: { locale: 'ar-SA', position: 'before' },
  EGP: { locale: 'ar-EG', position: 'before' },
};

// Default locale settings for currencies not in the mapping
const DEFAULT_LOCALE = { locale: 'en-US', position: 'before' as const };

// Build currency config map from countries.json
const buildCurrencyConfigMap = (): Record<string, CurrencyConfig> => {
  const currencyMap: Record<string, CurrencyConfig> = {};

  countriesData.forEach((country) => {
    const currency = country.currency as Currency;
    if (currency && currency.code && !currencyMap[currency.code]) {
      const localeConfig = CURRENCY_LOCALES[currency.code] || DEFAULT_LOCALE;
      currencyMap[currency.code] = {
        symbol: currency.symbol,
        locale: localeConfig.locale,
        symbolPosition: localeConfig.position,
      };
    }
  });

  return currencyMap;
};

// Currency configuration map (built once at module load)
export const CURRENCY_CONFIG = buildCurrencyConfigMap();

// Default currency
const DEFAULT_CURRENCY = 'EUR';

/**
 * Format a number as currency with the appropriate symbol and locale
 * @param value - The numeric value to format
 * @param currencyCode - ISO 4217 currency code (e.g., 'EUR', 'USD', 'GBP')
 * @returns Formatted currency string
 */
export function formatMoney(
  value: number | undefined | null,
  currencyCode: string = DEFAULT_CURRENCY
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }

  const config =
    CURRENCY_CONFIG[currencyCode?.toUpperCase()] ||
    CURRENCY_CONFIG[DEFAULT_CURRENCY];

  if (!config) {
    // Ultimate fallback if even EUR is not found
    return `€${value.toFixed(2)}`;
  }

  const formatted = new Intl.NumberFormat(config.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

  if (config.symbolPosition === 'before') {
    return `${config.symbol} ${formatted}`;
  }
  return `${formatted} ${config.symbol}`;
}

/**
 * Get currency symbol by currency code
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(
  currencyCode: string = DEFAULT_CURRENCY
): string {
  const config =
    CURRENCY_CONFIG[currencyCode?.toUpperCase()] ||
    CURRENCY_CONFIG[DEFAULT_CURRENCY];
  return config?.symbol || '€';
}
